import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../config/axios";
import { Reservation } from "../features/reservations/types/Reservation";
import { Acompanante } from "../types/Reservation";
import { logger } from "../utils/logger";
import { useDeleteReservationMutation } from "../contexts/MutationsContext";
import {
  beginReservationInvalidation,
  clearRecentlyDeletedReservation,
  endReservationInvalidation,
  invalidateReservationQueries,
  markLocalReservationInvalidation,
  markRecentlyDeletedReservation,
  removeReservationFromMonthCalendarCache,
  upsertReservationInMonthCalendarCache,
} from "../utils/queryInvalidation";

// Tipos para las mutaciones
export interface CreateReservationData {
  cliente_id: number;
  departamento: string;
  entrada: string;
  salida: string;
  valor_total: number;
  valor_original: number;
  pago: number;
  ajuste_financiero: number;
  tipo_ajuste?: string;
  cantidad_huespedes?: number;
  tipo_cama?: string[];
  late_checkout?: boolean;
  sin_limpieza_automatica?: boolean;
  estado: string;
  origen_reserva: string;
  notas?: string;
  nombre?: string;
  apellido?: string;
  email?: string;
  email1?: string;
  email2?: string;
  telefono?: string;
  telefono1?: string;
  telefono2?: string;
  dni?: string;
  localidad?: string;
  acompanantes?: Acompanante[];
  afiliado_nombre?: string;
  afiliado_apellido?: string;
  afiliado_dni?: string;
  hora_inicio?: string;
  hora_fin?: string;
  es_empresa?: boolean;
  codigo_reserva_externa?: string | null;
  codigo_booking?: string | null;
}

export interface UpdateReservationData extends Partial<CreateReservationData> {
  id: number;
  politica_precio?: string;
  precio_personalizado?: string | number;
  tipo_precio_personalizado?: string;
  dias_limpieza_pactados?: string[] | null;
  /** Días pactados que además tienen recambio de huésped. Informativo. */
  dias_limpieza_recambio?: string[] | null;
}

type ReservationMutationResponse =
  | Reservation
  | {
      reserva?: Reservation;
    };

const unwrapReservationResponse = (
  response: ReservationMutationResponse
): Reservation => {
  if ("reserva" in response && response.reserva) return response.reserva;
  return response as Reservation;
};

// Función para crear una reserva
const createReservation = async (
  data: CreateReservationData
): Promise<Reservation> => {
  const response = await api.post<ReservationMutationResponse>(
    "/reservas",
    data
  );
  return unwrapReservationResponse(response.data);
};

// Función para actualizar una reserva
const updateReservation = async (
  data: UpdateReservationData
): Promise<Reservation> => {
  const { id, ...updateData } = data;
  const response = await api.put<ReservationMutationResponse>(
    `/reservas/${id}`,
    updateData
  );
  return unwrapReservationResponse(response.data);
};

// Función para eliminar una reserva
const deleteReservation = async (id: number): Promise<void> => {
  await api.delete(`/reservas/${id}`);
};

// Función para cambiar cliente de una reserva (MODO DIOS)
const changeReservationClient = async (
  id: number,
  clienteId: number
): Promise<unknown> => {
  const response = await api.put(`/reservas/${id}/cliente`, {
    cliente_id: clienteId,
  });
  return response.data;
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }
  return undefined;
};

// Hook para crear reservas
export const useCreateReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createReservation,
    onSuccess: (data) => {
      markLocalReservationInvalidation(queryClient);
      const cacheUpdated = upsertReservationInMonthCalendarCache(
        queryClient,
        data
      );
      if (!cacheUpdated) {
        invalidateReservationQueries(queryClient, data);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-reservations"] });
    },
    onError: (error) => {
      logger.error("Error al crear reserva:", error);
    },
  });
};

// Hook para actualizar reservas
export const useUpdateReservation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateReservation,
    onSuccess: (data) => {
      const previousReservation = queryClient.getQueryData<Reservation>([
        "reservation",
        data.id,
      ]);
      markLocalReservationInvalidation(queryClient);
      queryClient.setQueryData(["reservation", data.id], data);
      const cacheUpdated = upsertReservationInMonthCalendarCache(
        queryClient,
        data
      );
      if (!cacheUpdated) {
        invalidateReservationQueries(
          queryClient,
          previousReservation ? [previousReservation, data] : undefined
        );
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-reservations"] });
    },
    onError: (error) => {
      logger.error("Error al actualizar reserva:", error);
    },
  });
};

// Hook para eliminar reservas usando Context API
export const useDeleteReservation = () => {
  const queryClient = useQueryClient();
  const { deleteReservationMutation, setDeleteReservationMutation } =
    useDeleteReservationMutation();

  // 🔥 CONTEXT API: Crear mutación solo si no existe
  if (!deleteReservationMutation) {
    const newMutation = useMutation({
      mutationFn: deleteReservation,
      onSuccess: (_data, reservationId) => {
        markLocalReservationInvalidation(queryClient);
        markRecentlyDeletedReservation(queryClient);
        const cacheUpdated = removeReservationFromMonthCalendarCache(
          queryClient,
          reservationId
        );
        queryClient.removeQueries({ queryKey: ["reservation", reservationId] });

        if (!cacheUpdated) {
          beginReservationInvalidation(queryClient);
          invalidateReservationQueries(queryClient);
        } else {
          queryClient.invalidateQueries({ queryKey: ["reservations"] });
          queryClient.invalidateQueries({ queryKey: ["calendar-reservations"] });
        }

        setTimeout(() => {
          endReservationInvalidation(queryClient);
          clearRecentlyDeletedReservation(queryClient);
        }, 2000);
      },
      onError: (error: unknown) => {
        // No mostrar error si es 404 (reserva ya eliminada por WebSocket)
        if (getErrorStatus(error) !== 404) {
          logger.error("Error al eliminar reserva:", error);
        }
      },
    });

    // 🔥 CONTEXT API: Registrar la mutación en el contexto
    setDeleteReservationMutation(newMutation);
    return newMutation;
  }

  return deleteReservationMutation;
};

// Hook para cambiar cliente de reserva (MODO DIOS)
export const useChangeReservationClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, clienteId }: { id: number; clienteId: number }) =>
      changeReservationClient(id, clienteId),
    onSuccess: () => {
      invalidateReservationQueries(queryClient);
    },
    onError: (error) => {
      logger.error("Error al cambiar cliente de reserva:", error);
    },
  });
};

// Hook combinado para todas las mutaciones de reservas
export const useReservationMutations = () => {
  const createMutation = useCreateReservation();
  const updateMutation = useUpdateReservation();
  const deleteMutation = useDeleteReservation();
  const changeClientMutation = useChangeReservationClient();

  return {
    createReservation: createMutation.mutate,
    updateReservation: updateMutation.mutate,
    deleteReservation: deleteMutation.mutate,
    changeReservationClient: changeClientMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isChangingClient: changeClientMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    changeClientError: changeClientMutation.error,
  };
};
